import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Annotation } from '../types';
import { logAuditEvent } from '../hooks/useAuditLog';

interface AnnotationUser {
  uid: string;
  email: string;
}

// Build a stable document identity shared by every user who opens the same file.
// Tree clicks produce full paths ("appfolder/0000/m3/.../file.pdf") while hyperlink
// navigation produces module-relative paths ("m3/.../file.pdf") - normalize both:
// strip the app folder and the 4-digit sequence folder, keep the module-relative rest.
export function buildDocKey(appId: string | undefined, sequenceId: string, path: string): string {
  const app = (appId || 'app').toLowerCase();
  let parts = path.toLowerCase().replace(/\\/g, '/').split('/').filter(Boolean);
  if (parts.length > 1 && parts[0] === app) parts = parts.slice(1);
  if (parts.length > 1 && /^\d{4}$/.test(parts[0])) parts = parts.slice(1);
  return `${app}::${(sequenceId || '').toLowerCase()}::${parts.join('/')}`;
}

// Live subscription to all annotations for one document; fires on every change
// from any user. Returns the unsubscribe function.
export function subscribeToAnnotations(
  docKey: string,
  callback: (annotations: Annotation[]) => void
): () => void {
  const q = query(collection(db, 'annotations'), where('docKey', '==', docKey));
  return onSnapshot(
    q,
    (snapshot) => {
      const items = snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          docKey: data.docKey,
          appId: data.appId,
          sequenceId: data.sequenceId,
          docPath: data.docPath,
          docTitle: data.docTitle,
          page: data.page,
          text: data.text,
          authorUid: data.authorUid,
          authorEmail: data.authorEmail,
          createdAt: data.createdAt?.toDate() || new Date(),
          resolved: !!data.resolved,
          resolvedBy: data.resolvedBy,
          resolvedAt: data.resolvedAt?.toDate(),
        } as Annotation;
      });
      // Sort client-side (page, then creation time) to avoid composite indexes
      items.sort((a, b) => a.page - b.page || a.createdAt.getTime() - b.createdAt.getTime());
      callback(items);
    },
    (error) => {
      console.error('Annotation subscription failed:', error);
      callback([]);
    }
  );
}

// Live subscription to every annotation in an application - powers the
// comment indicators in the document tree so reviewers can see which
// documents carry open comments without opening each one.
export function subscribeToAppAnnotations(
  appId: string,
  callback: (annotations: Annotation[]) => void
): () => void {
  const q = query(collection(db, 'annotations'), where('appId', '==', appId));
  return onSnapshot(
    q,
    (snapshot) => {
      callback(
        snapshot.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            docKey: data.docKey,
            appId: data.appId,
            sequenceId: data.sequenceId,
            docPath: data.docPath,
            docTitle: data.docTitle,
            page: data.page,
            text: data.text,
            authorUid: data.authorUid,
            authorEmail: data.authorEmail,
            createdAt: data.createdAt?.toDate() || new Date(),
            resolved: !!data.resolved,
            resolvedBy: data.resolvedBy,
            resolvedAt: data.resolvedAt?.toDate(),
          } as Annotation;
        })
      );
    },
    (error) => {
      console.error('App annotation subscription failed:', error);
      callback([]);
    }
  );
}

export async function addAnnotation(params: {
  docKey: string;
  appId: string;
  sequenceId: string;
  docPath: string;
  docTitle: string;
  page: number;
  text: string;
  user: AnnotationUser;
}): Promise<void> {
  const { user, ...rest } = params;
  await addDoc(collection(db, 'annotations'), {
    ...rest,
    authorUid: user.uid,
    authorEmail: user.email,
    resolved: false,
    createdAt: serverTimestamp(),
  });
  await logAuditEvent(user, 'annotation_add', {
    documentTitle: params.docTitle,
    page: params.page,
    textPreview: params.text.slice(0, 120),
  });
}

export async function setAnnotationResolved(
  annotation: Annotation,
  resolved: boolean,
  user: AnnotationUser
): Promise<void> {
  await updateDoc(doc(db, 'annotations', annotation.id), {
    resolved,
    resolvedBy: resolved ? user.email : null,
    resolvedAt: resolved ? serverTimestamp() : null,
  });
  await logAuditEvent(user, resolved ? 'annotation_resolve' : 'annotation_reopen', {
    documentTitle: annotation.docTitle,
    page: annotation.page,
    author: annotation.authorEmail,
  });
}

export async function deleteAnnotation(annotation: Annotation, user: AnnotationUser): Promise<void> {
  await deleteDoc(doc(db, 'annotations', annotation.id));
  await logAuditEvent(user, 'annotation_delete', {
    documentTitle: annotation.docTitle,
    page: annotation.page,
    author: annotation.authorEmail,
    textPreview: annotation.text.slice(0, 120),
  });
}
